import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { ApiError } from '@/api/client';
import { useConfirmEmail, useResendEmailConfirmation } from '@/api/queries/auth';
import { Button, buttonClasses } from '@pixlide/ui';
import { AuthLayout, FormAlert } from '@/features/auth/AuthLayout';
import { CodeInput } from '@/features/auth/CodeInput';
import { CONFIRMATION_CODE_LENGTH } from '@/features/auth/schemas';

/**
 * Messages des refus possibles, indexés par code d'erreur de l'API.
 * Le message brut du serveur reste le repli : mieux vaut un texte non retravaillé qu'un
 * écran muet si un nouveau code d'erreur apparaît.
 */
const ERROR_MESSAGES: Record<string, string> = {
  'user.confirmation_invalid': "Ce code n'est pas valide. Vérifiez les chiffres saisis.",
  'user.confirmation_expired': 'Ce code a expiré. Demandez-en un nouveau ci-dessous.',
  'user.confirmation_attempts_exceeded':
    'Trop de codes erronés ont été saisis. Demandez un nouveau code ci-dessous.',
  'user.no_confirmation_pending':
    "Aucune confirmation n'est en attente pour cette adresse. Demandez un nouveau code.",
};

/**
 * Écran de confirmation d'adresse : saisie du code reçu par email.
 *
 * Route à part entière, et non un simple état de la page d'inscription. La différence est
 * loin d'être cosmétique : le compte EXISTE déjà quand cet écran s'affiche, mais ne peut
 * pas encore se connecter. Sans URL propre, un rechargement ou un onglet fermé faisait
 * disparaître l'écran sans aucun moyen d'y revenir — se réinscrire échoue, l'adresse étant
 * prise, et se connecter échoue, l'adresse n'étant pas confirmée. L'utilisateur était
 * enfermé.
 *
 * L'adresse voyage donc dans l'URL. Elle n'a rien d'un secret : c'est celle que la personne
 * vient de saisir, et le code, lui, reste dans la boîte mail.
 */
export function ConfirmEmailPage() {
  const { email } = useSearch({ from: '/confirmation-email' });
  const navigate = useNavigate();
  const confirm = useConfirmEmail();
  const resend = useResendEmailConfirmation();

  const [code, setCode] = useState('');

  // Message produit par l'écran lui-même, distinct de celui du serveur : il couvre le cas
  // d'un code incomplet, que l'API n'a pas à arbitrer.
  const [localError, setLocalError] = useState<string | null>(null);

  async function verify() {
    if (code.length !== CONFIRMATION_CODE_LENGTH) {
      setLocalError(`Saisissez les ${CONFIRMATION_CODE_LENGTH} chiffres du code reçu par email.`);

      return;
    }

    setLocalError(null);
    await confirm.mutateAsync({ email, code });

    // L'adresse est confirmée : le compte peut désormais se connecter. On envoie sur la
    // connexion plutôt que d'ouvrir une session, car l'inscription n'a jamais délivré de
    // jeton — c'est le mot de passe qui reste la preuve d'identité.
    await navigate({ to: '/connexion', search: {} });
  }

  const apiError = confirm.error instanceof ApiError ? confirm.error : null;

  const alertMessage =
    localError ?? (apiError === null ? null : (ERROR_MESSAGES[apiError.code] ?? apiError.message));

  // URL sans adresse : la page est atteignable directement, elle doit donc dire quoi faire
  // plutôt que d'afficher quatre cases inutilisables — renvoyer un code exige de savoir à
  // qui.
  if (email === '') {
    return (
      <AuthLayout
        title="Adresse introuvable"
        subtitle="Ce lien de confirmation ne précise pas quelle adresse confirmer."
      >
        <p className="text-[13px] leading-relaxed text-ink-500">
          Connectez-vous avec votre adresse : si elle n'est pas encore confirmée, nous vous
          proposerons d'en recevoir un nouveau code.
        </p>

        <Link
          to="/connexion"
          search={{}}
          className={buttonClasses({ className: 'mt-6 h-11 w-full rounded-xl' })}
        >
          Aller à la connexion
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Veuillez consulter votre boîte mail"
      subtitle={`Nous avons envoyé un code à ${email}`}
    >
      {alertMessage ? <FormAlert message={alertMessage} /> : null}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void verify();
        }}
        className="flex flex-col gap-6"
      >
        <CodeInput
          value={code}
          onChange={(next) => {
            setCode(next);

            // Le message d'erreur porte sur le code précédent : le garder affiché pendant
            // la correction laisserait croire que la nouvelle saisie est déjà refusée.
            setLocalError(null);
            confirm.reset();
          }}
          length={CONFIRMATION_CODE_LENGTH}
          disabled={confirm.isPending}
          invalid={alertMessage !== null}
        />

        <p aria-live="polite" className="text-[11px] text-ink-500">
          {resend.isSuccess ? (
            <span className="font-medium text-ink-900">
              Un nouveau code vient d'être envoyé à {email}.
            </span>
          ) : (
            <>
              Vous n'avez pas reçu de code ?{' '}
              <button
                type="button"
                onClick={() => resend.mutate({ email })}
                disabled={resend.isPending}
                className="inline-flex items-center gap-1 font-semibold text-ink-900 hover:underline disabled:opacity-60"
              >
                Renvoyer
                <ArrowRight aria-hidden className="size-3" />
              </button>
            </>
          )}
        </p>

        <div className="flex gap-3">
          {/* « Annuler » ramène à la CONNEXION et non à l'inscription : le compte est déjà
              créé, se réinscrire échouerait sur une adresse déjà prise. Et si la connexion
              est tentée avant confirmation, elle propose elle-même de revenir ici. */}
          <Link
            to="/connexion"
            search={{}}
            className={buttonClasses({
              variant: 'secondary',
              className: 'h-11 flex-1 rounded-xl',
            })}
          >
            Annuler
          </Link>

          <Button
            type="submit"
            // Volontairement toujours actif : un bouton grisé n'explique pas ce qui manque,
            // alors qu'un clic sur un code incomplet affiche le message correspondant.
            isLoading={confirm.isPending}
            className="h-11 flex-1 rounded-xl"
          >
            Vérifiez
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
